/* Card — the panel's single surface primitive.

   Colour is OPT-IN via the `tone` prop. Without it a card is white.

   This is deliberate: Card wraps both small KPI tiles AND large page-level
   containers (table wrappers, form panels, filter boxes). Tinting by section
   coloured everything, including the big containers, which turned the page
   background itself coloured instead of highlighting the cards on it. The
   page stays white; the cards sit on it in colour.

   Tones are 100-shade backgrounds with 300-shade rings — bright enough to
   read as coloured blocks rather than a wash. Pair with TONE_ACCENT below for
   the icon chip and numeral so a tile reads at a glance.

   Class strings are literal, never composed at runtime: Tailwind's JIT only
   generates classes it can find in source, so `bg-${tone}-100` would silently
   render unstyled. */
const TONES = {
  default: "bg-white ring-slate-300",
  info:    "bg-blue-100 ring-blue-300",
  success: "bg-emerald-100 ring-emerald-300",
  warning: "bg-amber-100 ring-amber-300",
  danger:  "bg-red-100 ring-red-300",
  muted:   "bg-slate-100 ring-slate-300",
  // Non-semantic hues — for cards that are foundational or categorical rather
  // than a status. Kept outside the RAG set so they can never be misread as
  // "this needs attention".
  indigo:  "bg-indigo-100 ring-indigo-300",
  violet:  "bg-violet-100 ring-violet-300",
  teal:    "bg-teal-100 ring-teal-300",
  // amber, not the brand gold scale. Tailwind here is v4, which ignores
  // theme.extend.colors in tailwind.config.js — every gold-* and navy-*
  // utility silently generates nothing (hence the hardcoded bg-[#162a52]
  // elsewhere in this codebase). amber is built in, always works, and reads
  // as gold without the config dependency.
  //
  // gold-200 (#e7cd8b) was tried first and drowned the text: the other tones
  // are 50/100-shade pastels, while that is a solid colour. A tinted surface
  // has to stay light enough for slate-400 labels to remain legible.
  // Navy ring on a warm surface — the navy already carries the sidebar,
  // primary buttons and headings, so the pairing reads as brand rather than
  // a stray accent. Hex literal because navy-* generates nothing under v4.
  gold:    "bg-amber-50 ring-[#162a52]",
};

/* Accents for content inside a toned card — a solid chip behind the icon and
   a coloured numeral. Text is 800-shade so it stays legible on the brighter
   100-shade surfaces. */
export const TONE_ACCENT = {
  default: { chip: "bg-slate-400",   text: "text-slate-900" },
  info:    { chip: "bg-blue-600",    text: "text-blue-800" },
  success: { chip: "bg-emerald-600", text: "text-emerald-800" },
  warning: { chip: "bg-amber-600",   text: "text-amber-800" },
  danger:  { chip: "bg-red-600",     text: "text-red-800" },
  muted:   { chip: "bg-slate-400",   text: "text-slate-600" },
  indigo:  { chip: "bg-indigo-600",  text: "text-indigo-800" },
  violet:  { chip: "bg-violet-600",  text: "text-violet-800" },
  teal:    { chip: "bg-teal-600",    text: "text-teal-800" },
  // gold-500 / gold-700 generate nothing under v4 — see the note above.
  gold:    { chip: "bg-amber-600",   text: "text-amber-800" },
};

export function Card({ children, className = "", tone = "default", onClick, onMouseDown }) {
  const toneCls = TONES[tone] || TONES.default;
  return (
    <div
      className={`${toneCls} rounded-2xl ring-1 ${className}`}
      onClick={onClick}
      onMouseDown={onMouseDown}
    >
      {children}
    </div>
  );
}
